import axios from "axios";

export const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const http = axios.create({ baseURL: `${BACKEND_URL}/api`, withCredentials: true });

const csrf = () => document.cookie.split("; ").find((c) => c.startsWith("forge_csrf="))?.split("=")[1];
http.interceptors.request.use((cfg) => {
  const t = csrf();
  if (t) cfg.headers["X-CSRF-Token"] = t;
  return cfg;
});
let refreshing = null;
http.interceptors.response.use(null, async (err) => {
  const cfg = err.config || {};
  const url = cfg.url || "";
  if (err.response?.status === 401 && !cfg._retry && !url.startsWith("/auth/")) {
    cfg._retry = true;
    try {
      refreshing = refreshing || http.post("/auth/refresh").finally(() => { refreshing = null; });
      await refreshing;
      return http(cfg);
    } catch (e) { /* fall through */ }
  }
  return Promise.reject(err);
});

export const errMsg = (e) => {
  const d = e?.response?.data?.detail;
  if (typeof d === "string") return d;
  if (Array.isArray(d)) return d.map((x) => x?.msg || JSON.stringify(x)).join(" ");
  if (d?.message) return d.message;
  return e?.message || "Request failed";
};

export const auth = {
  captcha: () => http.get("/auth/captcha").then((r) => r.data),
  login: (body) => http.post("/auth/login", body).then((r) => r.data),
  signup: (body) => http.post("/auth/signup", body).then((r) => r.data),
  logout: () => http.post("/auth/logout").then((r) => r.data),
  me: () => http.get("/auth/me").then((r) => r.data),
  session: () => http.get("/auth/session").then((r) => r.data.user || false),
  updateMe: (body) => http.put("/auth/me", body).then((r) => r.data),
  workspace: () => http.get("/workspace").then((r) => r.data),
  updateWorkspace: (name) => http.put("/workspace", { name }).then((r) => r.data),
  transcribe: (blob) => {
    const fd = new FormData();
    fd.append("audio", blob, "voice.webm");
    return http.post("/voice/transcribe", fd).then((r) => r.data);
  },
};

export const api = {
  listProjects: () => http.get("/projects").then((r) => r.data),
  getProject: (id) => http.get(`/projects/${id}`).then((r) => r.data),
  createProject: (body) => http.post("/projects", body).then((r) => r.data),
  deleteProject: (id) => http.delete(`/projects/${id}`).then((r) => r.data),
  genBrief: (id) => http.post(`/projects/${id}/brief`, null, { timeout: 300000 }).then((r) => r.data),
  putBrief: (id, brief) => http.put(`/projects/${id}/brief`, { brief }).then((r) => r.data),
  genPlan: (id) => http.post(`/projects/${id}/plan`, null, { timeout: 300000 }).then((r) => r.data),
  putPlan: (id, body) => http.put(`/projects/${id}/plan`, body).then((r) => r.data),
  approve: (id) => http.post(`/projects/${id}/approve`).then((r) => r.data),
  verify: (id, body) => http.post(`/projects/${id}/verify`, body).then((r) => r.data),
  getRun: (id, runId) => http.get(`/projects/${id}/builds/${runId}`).then((r) => r.data),
  listRuns: (id) => http.get(`/projects/${id}/builds`).then((r) => r.data),
  getVersion: (id, v) => http.get(`/projects/${id}/versions/${v}`).then((r) => r.data),
  restoreVersion: (id, v) => http.post(`/projects/${id}/versions/${v}/restore`).then((r) => r.data),
  intent: (id) => http.post(`/projects/${id}/intent`, null, { timeout: 300000 }).then((r) => r.data),
  answerIntent: (id, body) => http.put(`/projects/${id}/intent`, body).then((r) => r.data),
  estimate: (kind, model) => http.get("/estimate", { params: { kind, model } }).then((r) => r.data),
  usage: (id) => http.get(`/projects/${id}/usage`).then((r) => r.data),
  logs: (id) => http.get(`/projects/${id}/logs`).then((r) => r.data),
};

export const twin = {
  constitution: (id, version) => http.get(`/projects/${id}/constitution`, { params: version ? { version } : {} }).then((r) => r.data),
  versions: (id) => http.get(`/projects/${id}/constitution/versions`).then((r) => r.data),
  diff: (id, a, b) => http.get(`/projects/${id}/constitution/diff`, { params: { a, b } }).then((r) => r.data),
  explain: (body) => http.post("/explain", body, { timeout: 120000 }).then((r) => r.data),
  apps: () => http.get("/apps").then((r) => r.data),
};

export const changes = {
  create: (id, request) => http.post(`/projects/${id}/changes`, { request }).then((r) => r.data),
  list: (id) => http.get(`/projects/${id}/changes`).then((r) => r.data),
  get: (id, cid) => http.get(`/projects/${id}/changes/${cid}`).then((r) => r.data),
  select: (id, cid, ids) => http.post(`/projects/${id}/changes/${cid}/select`, { proposal_ids: ids }).then((r) => r.data),
  act: (id, cid, action, body) => http.post(`/projects/${id}/changes/${cid}/${action}`, body).then((r) => r.data),
};

export const copilot = {
  history: (id) => http.get(`/projects/${id}/copilot/history`).then((r) => r.data),
  ask: (id, message) => http.post(`/projects/${id}/copilot`, { message }, { timeout: 180000 }).then((r) => r.data),
};

const RT = { headers: { "X-Forge-Source": "explorer" } };
export const runtime = {
  schema: (pid) => http.get(`/runtime/${pid}/schema`).then((r) => r.data),
  list: (pid, col) => http.get(`/runtime/${pid}/data/${col}`, { ...RT, params: { _sort: "created_at", _order: "desc", _limit: 500 } }).then((r) => r.data),
  create: (pid, col, rec) => http.post(`/runtime/${pid}/data/${col}`, rec, RT).then((r) => r.data),
  update: (pid, col, rid, rec) => http.put(`/runtime/${pid}/data/${col}/${rid}`, rec, RT).then((r) => r.data),
  remove: (pid, col, rid) => http.delete(`/runtime/${pid}/data/${col}/${rid}`, RT).then((r) => r.data),
  chat: (pid, message, session_id) => http.post(`/runtime/${pid}/agent/chat`, { message, session_id }, { timeout: 120000 }).then((r) => r.data),
};

export const agents = {
  list: () => http.get("/agents").then((r) => r.data),
  create: (body) => http.post("/agents", body).then((r) => r.data),
  update: (id, body) => http.put(`/agents/${id}`, body).then((r) => r.data),
  remove: (id) => http.delete(`/agents/${id}`).then((r) => r.data),
  updateApp: (pid, body) => http.put(`/agents/app/${pid}`, body).then((r) => r.data),
  conversations: (a) => http.get(a.type === "app" ? `/agents/app/${a.project_id}/conversations` : `/agents/${a.id}/conversations`).then((r) => r.data),
  chat: (id, message, session_id) => http.post(`/public/agents/${id}/chat`, { message, session_id }, { timeout: 120000 }).then((r) => r.data),
};

export const ruleError = (e) => {
  const d = e?.response?.data?.detail;
  return d?.error === "rule_violation" ? `Rule "${d.rule}" on ${d.field}: ${d.message}` : errMsg(e);
};

const g = (u, params) => http.get(u, { params }).then((r) => r.data);
const p = (u, b, t) => http.post(u, b, t ? { timeout: t } : undefined).then((r) => r.data);
export const connectApi = {
  testEmail: (to) => p("/admin/providers/resend/test-email", { to }, 60000),
  integrations: () => g("/integrations"),
  providers: () => g("/admin/providers"),
  saveProvider: (k, credentials) => http.put(`/admin/providers/${k}`, { credentials }).then((r) => r.data),
  health: (k) => p(`/admin/providers/${k}/health`),
  rates: () => g("/admin/rate-card"),
  sender: () => g("/admin/email-sender"),
  saveSender: (b) => http.put("/admin/email-sender", b).then((r) => r.data),
  saveRates: (b) => http.put("/admin/rate-card", b).then((r) => r.data),
  messages: (params) => g("/messages", params),
  usage: (project_id) => g("/usage/summary", project_id ? { project_id } : {}),
  stats: (pid) => g(`/projects/${pid}/connect/stats`),
  campaigns: () => g("/campaigns"),
  campaign: (id) => g(`/campaigns/${id}`),
  createCampaign: (b) => p("/campaigns", b),
  addRecipients: (id, b) => p(`/campaigns/${id}/recipients`, b),
  testCampaign: (id, b) => p(`/campaigns/${id}/test`, b, 60000),
  sendCampaign: (id) => p(`/campaigns/${id}/send`, {}, 120000),
  assist: (b) => p("/campaigns/assist", b, 90000),
};
export const autoApi = {
  list: (project_id) => g("/automations", project_id ? { project_id } : {}),
  draft: (b) => p("/automations/draft", b, 120000),
  create: (b) => p("/automations", b),
  update: (id, b) => http.put(`/automations/${id}`, b).then((r) => r.data),
  remove: (id) => http.delete(`/automations/${id}`).then((r) => r.data),
  dry: (id, record) => p(`/automations/${id}/dry-run`, { record }),
  run: (id, record) => p(`/automations/${id}/run`, { record }, 120000),
  runs: (id) => g(`/automations/${id}/runs`),
  notes: () => g("/automations/drafts/notes"),
};

export const opsApi = {
  workspace: () => g("/workspace/settings"),
  saveWorkspace: (b) => http.put("/workspace/settings", b).then((r) => r.data),
  retention: () => g("/admin/retention"),
  saveRetention: (days) => http.put("/admin/retention", { days }).then((r) => r.data),
  exportUrl: (path) => `${BACKEND_URL}/api${path}`,
  upload: (file, kind, onProgress) => {
    const fd = new FormData();
    fd.append("file", file);
    fd.append("kind", kind);
    return http.post("/media", fd, { timeout: 600000, onUploadProgress: (e) => onProgress?.(e.total ? Math.round((e.loaded / e.total) * 100) : 0) }).then((r) => r.data);
  },
  orphans: (pid) => g(`/projects/${pid}/orphan-fields`),
  cleanup: (pid, expected_records) => p(`/projects/${pid}/orphan-fields/cleanup`, { confirm: true, expected_records }),
};

const G = "/admin/growth";
export const growthApi = {
  meta: () => g(`${G}/meta`),
  voice: () => g(`${G}/voice`),
  saveVoice: (b) => http.put(`${G}/voice`, b).then((r) => r.data),
  campaigns: () => g(`${G}/campaigns`),
  createCampaign: (b) => p(`${G}/campaigns`, b),
  generate: (b) => p(`${G}/generate`, b, 240000),
  posts: (params) => g(`${G}/posts`, params),
  update: (id, b) => http.put(`${G}/posts/${id}`, b).then((r) => r.data),
  approve: (id) => p(`${G}/posts/${id}/approve`),
  schedule: (id, scheduled_at) => p(`${G}/posts/${id}/schedule`, { scheduled_at }),
  publishNow: (id) => p(`${G}/posts/${id}/publish-now`, {}, 60000),
  image: (id) => p(`${G}/posts/${id}/image`, {}, 180000),
  remove: (id) => http.delete(`${G}/posts/${id}`).then((r) => r.data),
  utm: (b) => p(`${G}/utm`, b),
  performance: () => g(`${G}/performance`),
};

export const mobileApi = {
  hub: (pid) => g(`/projects/${pid}/mobile`),
  storeKit: (pid) => p(`/projects/${pid}/mobile/store-kit`, {}),
  creds: (pid, store, values) => http.put(`/projects/${pid}/mobile/credentials`, { store, values }).then((r) => r.data),
  config: (pid, b) => http.put(`/projects/${pid}/mobile/config`, b).then((r) => r.data),
  build: (pid, platform) => p(`/projects/${pid}/mobile/builds`, { platform, profile: "production" }),
  submit: (pid, platform) => p(`/projects/${pid}/mobile/submit`, { platform }),
};

export const billingApi = {
  plans: () => g("/billing/plans"),
  models: () => g("/billing/models"),
  summary: () => g("/billing/summary"),
  checkout: (b) => p("/billing/checkout", { ...b, origin_url: window.location.origin }, 60000),
  status: (sid) => g(`/payments/status/${sid}`),
  changePlan: (plan, yearly) => p("/billing/change-plan", { plan, yearly }, 60000),
  cancel: () => p("/billing/cancel", {}, 60000),
  resume: () => p("/billing/resume", {}, 60000),
  invoices: () => g("/billing/invoices"),
  wallet: () => g("/billing/wallet"),
  autoRecharge: (b) => http.put("/billing/auto-recharge", b).then((r) => r.data),
  publishRequest: (b) => p("/publish-requests", b),
  publishRequests: () => g("/publish-requests"),
  revenue: () => g("/admin/revenue"),
  override: (email, plan) => http.put("/admin/billing/override", { email, plan }).then((r) => r.data),
  adminPlans: () => g("/admin/plans"),
  saveAdminPlans: (b) => http.put("/admin/plans", b, { timeout: 120000 }).then((r) => r.data),
  adminPublish: () => g("/admin/publish-requests"),
  setPublish: (id, status, note) => http.put(`/admin/publish-requests/${id}`, { status, note }).then((r) => r.data),
  router: () => g("/admin/model-router"),
  saveRouter: (b) => http.put("/admin/model-router", b).then((r) => r.data),
};
